import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { logoPecsmart } from '../base64/logoPecsmart';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

pdfMake.vfs = pdfFonts?.pdfMake?.vfs;

export const generateMultipleSilosPDF = async (dataBySilo, locationInfo) => {
  if (!dataBySilo || Object.keys(dataBySilo).length === 0) {
    console.error('No hay datos para exportar');
    return;
  }

  // Obtener el rango de fechas de todos los silos
  const allDates = Object.values(dataBySilo).flat().map(row => new Date(row.datetime_fetched));
  const startDate = new Date(Math.min(...allDates)).toLocaleDateString();
  const endDate = new Date(Math.max(...allDates)).toLocaleDateString();
  
  // Crear nombre del archivo con la información de ubicación
  const siloCount = Object.keys(dataBySilo).length;
  const fileName = `Dados de Silo - ${locationInfo.farmName} - ${locationInfo.barnName} - ${siloCount} Silo(s) - ${startDate} - ${endDate}.pdf`;

  const content = [];

  // Agregar header común
  content.push(
    {
      columns: [
        {
          image: logoPecsmart,
          width: 120,
          margin: [0, 0, 0, 20],
          alignment: 'center'
        }
      ],
      alignment: 'center'
    },
    {
      text: 'Dados de Silo',
      style: 'mainHeader',
      alignment: 'center',
      margin: [0, 0, 0, 5]
    },
    {
      text: `${locationInfo.farmName} - ${locationInfo.barnName}`,
      style: 'locationInfo',
      alignment: 'center',
      margin: [0, 0, 0, 5]
    },
    {
      text: `${startDate} - ${endDate}`,
      style: 'subHeader',
      alignment: 'center',
      margin: [0, 0, 0, 30]
    }
  );

  // Crear contenido para cada silo
  Object.entries(dataBySilo).forEach(([siloName, data]) => {
    if (data.length === 0) return;

    // Subtítulo del silo alineado a la izquierda
    content.push({
      text: `Relatório - ${siloName}`,
      style: 'siloHeader',
      margin: [0, 20, 0, 10]
    });

    // Crear tabla para este silo
    const tableBody = [
      [
        { text: 'Silo', style: 'tableHeader', alignment: 'center' },
        { text: 'Data/Hora', style: 'tableHeader', alignment: 'center' },
        { text: 'Peso (kg)', style: 'tableHeader', alignment: 'center' },
        { text: 'Nível (%)', style: 'tableHeader', alignment: 'center' },
        { text: 'Volume (m³)', style: 'tableHeader', alignment: 'center' },
        { text: 'Matéria Prima', style: 'tableHeader', alignment: 'center' },
        { text: 'Densidade (kg/m³)', style: 'tableHeader', alignment: 'center' }
      ]
    ];

    // Agregar datos del silo
    data.forEach((row) => {
      tableBody.push([
        { text: row.silo_name || `Silo ${row.silo || 'N/A'}`, alignment: 'center' },
        { text: row.datetime_fetched ? format(new Date(row.datetime_fetched), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A', alignment: 'center' },
        { text: row.weight != null ? Number(row.weight.toFixed(2)) : '0', alignment: 'center' },
        { text: row.level != null ? Number(row.level.toFixed(2)) : '0', alignment: 'center' },
        { text: row.volume != null ? Number(row.volume.toFixed(2)) : '0', alignment: 'center' },
        { text: row.food_name || 'N/A', alignment: 'center' },
        { text: row.food_density != null ? Number(row.food_density.toFixed(2)) : 'N/A', alignment: 'center' }
      ]);
    });

    content.push({
      table: {
        headerRows: 1,
        widths: ['12%', '18%', '12%', '12%', '12%', '20%', '14%'],
        body: tableBody
      },
      layout: {
        fillColor: function(rowIndex) {
          return (rowIndex % 2 === 0) ? '#FFFFFF' : '#F8F9FA';
        }
      },
      margin: [0, 0, 0, 20]
    });
  });

  const documentDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 20],
    content: content,
    styles: {
      mainHeader: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 0]
      },
      locationInfo: {
        fontSize: 12,
        bold: true,
        margin: [0, 5, 0, 0]
      },
      subHeader: {
        fontSize: 12,
        italic: true,
        color: '#666666',
        margin: [0, 5, 0, 0]
      },
      siloHeader: {
        fontSize: 14,
        bold: true,
        color: '#1b4238',
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fontSize: 9,
        color: 'black',
        fillColor: '#eeeeee'
      }
    },
    defaultStyle: {
      fontSize: 9
    }
  };

  const pdfDoc = pdfMake.createPdf(documentDefinition);
  pdfDoc.download(fileName);
};

// Función para generar PDF de un solo silo (mantener compatibilidad)
export const generateSiloDataPDF = async (data, locationInfo) => {
  if (!data || data.length === 0) {
    console.error('No hay datos para exportar');
    return;
  }

  // Obtener el rango de fechas
  const dates = data.map(row => new Date(row.datetime_fetched));
  const startDate = new Date(Math.min(...dates)).toLocaleDateString();
  const endDate = new Date(Math.max(...dates)).toLocaleDateString();
  
  // Crear nombre del archivo con la información de ubicación
  const fileName = `Dados de Silo - ${locationInfo.farmName} - ${locationInfo.barnName} - Silo ${locationInfo.siloName} - ${startDate} - ${endDate}.pdf`;

  const content = [
    {
      columns: [
        {
          image: logoPecsmart,
          width: 120,
          margin: [0, 0, 0, 20],
          alignment: 'center'
        }
      ],
      alignment: 'center'
    },
    {
      text: 'Dados de Silo',
      style: 'mainHeader',
      alignment: 'center',
      margin: [0, 0, 0, 5]
    },
    {
      text: `${locationInfo.farmName} - ${locationInfo.barnName} - Silo ${locationInfo.siloName}`,
      style: 'locationInfo',
      alignment: 'center',
      margin: [0, 0, 0, 5]
    },
    {
      text: `${startDate} - ${endDate}`,
      style: 'subHeader',
      alignment: 'center',
      margin: [0, 0, 0, 30]
    },
    {
      text: `Relatório - Silo ${locationInfo.siloName}`,
      style: 'siloHeader',
      margin: [0, 0, 0, 10]
    }
  ];

  // Crear tabla
  const tableBody = [
    [
      { text: 'Silo', style: 'tableHeader', alignment: 'center' },
      { text: 'Data/Hora', style: 'tableHeader', alignment: 'center' },
      { text: 'Peso (kg)', style: 'tableHeader', alignment: 'center' },
      { text: 'Nível (%)', style: 'tableHeader', alignment: 'center' },
      { text: 'Volume (m³)', style: 'tableHeader', alignment: 'center' },
      { text: 'Matéria Prima', style: 'tableHeader', alignment: 'center' },
      { text: 'Densidade (kg/m³)', style: 'tableHeader', alignment: 'center' }
    ]
  ];

  // Agregar datos
  data.forEach((row) => {
    tableBody.push([
      { text: row.silo_name || `Silo ${row.silo || 'N/A'}`, alignment: 'center' },
      { text: row.datetime_fetched ? format(new Date(row.datetime_fetched), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A', alignment: 'center' },
      { text: row.weight != null ? Number(row.weight.toFixed(2)) : '0', alignment: 'center' },
      { text: row.level != null ? Number(row.level.toFixed(2)) : '0', alignment: 'center' },
      { text: row.volume != null ? Number(row.volume.toFixed(2)) : '0', alignment: 'center' },
      { text: row.food_name || 'N/A', alignment: 'center' },
      { text: row.food_density != null ? Number(row.food_density.toFixed(2)) : 'N/A', alignment: 'center' }
    ]);
  });

  content.push({
    table: {
      headerRows: 1,
      widths: ['12%', '18%', '12%', '12%', '12%', '20%', '14%'],
      body: tableBody
    },
    layout: {
      fillColor: function(rowIndex) {
        return (rowIndex % 2 === 0) ? '#FFFFFF' : '#F8F9FA';
      }
    }
  });

  const documentDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 20],
    content: content,
    styles: {
      mainHeader: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 0]
      },
      locationInfo: {
        fontSize: 12,
        bold: true,
        margin: [0, 5, 0, 0]
      },
      subHeader: {
        fontSize: 12,
        italic: true,
        color: '#666666',
        margin: [0, 5, 0, 0]
      },
      siloHeader: {
        fontSize: 14,
        bold: true,
        color: '#1b4238',
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fontSize: 9,
        color: 'black',
        fillColor: '#eeeeee'
      }
    },
    defaultStyle: {
      fontSize: 9
    }
  };

  const pdfDoc = pdfMake.createPdf(documentDefinition);
  pdfDoc.download(fileName);
};
